import { useState } from "react";
import { BrowserRouter, Routes, Route, NavLink, Navigate, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import {
  LayoutDashboard, Users as UsersIcon, FileText, ShieldCheck, ScrollText,
  Settings as SettingsIcon, LogOut, Building2, ShieldHalf, Menu, X, Lock, Repeat, Boxes, Radar, Landmark, Globe2,
} from "lucide-react";
import { AuthProvider, useAuth } from "./auth";
import { apiErr } from "./lib/api";
import { Btn, Input, Label, Card } from "./ui";
import { Dashboard, Clients, UsersPage, Approvals, Audit, Settings, Masters, TdsReport } from "./pages";
import { Invoices, InvoiceEditor, InvoiceDetail } from "./invoices";
import { ChangePasswordButton } from "./ChangePassword";
import ForgotPassword, { ResetPassword } from "./ForgotPassword";
import { Recurring, ExportRecurring } from "./recurring";
import { Watchlist } from "./security";
import { ExportInvoices, ExportInvoiceEditor, ExportInvoiceDetail, ExportClients } from "./exports";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, roles: ["super_admin", "admin"] },
  { to: "/invoices", label: "Invoices", icon: FileText },
  { to: "/exports", label: "Exports", icon: Globe2 },
  { to: "/recurring", label: "Recurring", icon: Repeat },
  { to: "/clients", label: "Clients", icon: Building2 },
  { to: "/masters", label: "Masters", icon: Boxes },
  { to: "/tds", label: "TDS Receivable", icon: Landmark, roles: ["super_admin", "admin"] },
  { to: "/approvals", label: "Approvals", icon: ShieldCheck, roles: ["super_admin", "admin"] },
  { to: "/users", label: "Users & Access", icon: UsersIcon, roles: ["super_admin", "admin"] },
  { to: "/audit", label: "Audit Trail", icon: ScrollText, roles: ["super_admin", "admin"] },
  { to: "/watchlist", label: "Watchlist", icon: Radar, roles: ["super_admin", "admin"] },
  { to: "/settings", label: "Security", icon: SettingsIcon, roles: ["super_admin", "admin"] },
];

function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [need2fa, setNeed2fa] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await login(email, password, totp || undefined);
      if (r.require_totp) { setNeed2fa(true); toast.info("Enter your 2FA code"); }
    } catch (err) { toast.error(apiErr(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-[#0B0F17] via-[#0F172A] to-[#111827]">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center text-center mb-6">
          <img data-testid="login-logo" src="/iws-logo.png" alt="IWS" className="h-16 w-auto mb-3" />
          <div className="font-heading font-extrabold text-xl text-slate-100 leading-none">IWS Secure Billing</div>
          <div className="text-xs font-mono text-slate-500 mt-1">Infomedia Web Solutions</div>
        </div>
        <Card className="p-8">
          <h1 className="text-2xl font-bold font-heading text-slate-100 mb-1">Sign in</h1>
          <p className="text-sm text-slate-400 mb-6">Access is monitored and audited.</p>
          <form onSubmit={submit} className="space-y-4">
            <div><Label>Email</Label><Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" /></div>
            <div><Label>Password</Label><Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" /></div>
            {need2fa && (
              <div className="animate-fade-up"><Label>2FA Code</Label>
                <Input data-testid="login-totp" className="font-mono tracking-[0.4em] text-center text-lg" value={totp} onChange={(e) => setTotp(e.target.value)} placeholder="000000" maxLength={6} />
                <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><Lock size={11} /> Required from outside the office network</p>
              </div>
            )}
            <Btn data-testid="login-submit" type="submit" disabled={busy} className="w-full">{busy ? "Signing in…" : need2fa ? "Verify & Sign in" : "Sign in"}</Btn>
            <a href="/forgot-password" data-testid="forgot-password-link" className="block text-center text-sm text-slate-400 hover:text-sky-300">Forgot password?</a>
          </form>
        </Card>
      </div>
    </div>
  );
}

function Shell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const items = NAV.filter((n) => !n.roles || n.roles.includes(user.role));
  const doLogout = async () => { await logout(); nav("/"); };
  const roleBadge = { super_admin: "text-rose-300 border-rose-500/30 bg-rose-500/10", admin: "text-sky-300 border-sky-500/30 bg-sky-500/10", staff: "text-slate-300 border-slate-500/30 bg-slate-500/10" }[user.role];

  const SideContent = (
    <>
      <div className="flex items-center gap-2 px-4 h-16 border-b border-slate-800">
        <div className="p-1.5 rounded-lg bg-sky-500/10 border border-sky-500/30"><ShieldHalf className="text-sky-400" size={18} /></div>
        <div><div className="font-heading font-extrabold text-slate-100 leading-none">IWS Billing</div>
          <div className="text-[10px] font-mono text-slate-500">SECURE CORE</div></div>
      </div>
      <nav className="p-3 space-y-1">
        {items.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)} data-testid={`nav-${n.label.toLowerCase().split(" ")[0]}`}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium [transition:color_.2s,background-color_.2s] ${isActive ? "bg-sky-500/10 text-sky-300 border border-sky-500/20" : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/60 border border-transparent"}`}>
            <n.icon size={18} /> {n.label}
          </NavLink>
        ))}
      </nav>
    </>
  );

  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100">
      {/* Sidebar desktop */}
      <aside className="hidden md:flex flex-col fixed inset-y-0 left-0 w-64 bg-[#0D1220] border-r border-slate-800 z-40">{SideContent}
        <div className="mt-auto p-3 border-t border-slate-800">
          <div className="px-3 py-2 rounded-lg bg-slate-900/60 mb-2">
            <div className="text-sm text-slate-200 truncate">{user.name}</div>
            <div className="text-xs font-mono text-slate-500 truncate">{user.email}</div>
            <span className={`inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-mono border ${roleBadge}`}>{user.role}</span>
          </div>
          <ChangePasswordButton />
          <Btn data-testid="logout-btn" variant="ghost" className="w-full" onClick={doLogout}><LogOut size={16} /> Sign out</Btn>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden sticky top-0 z-50 flex items-center justify-between h-14 px-4 bg-[#0B0F17]/90 backdrop-blur-xl border-b border-slate-800">
        <div className="flex items-center gap-2"><ShieldHalf className="text-sky-400" size={18} /><span className="font-heading font-bold">IWS Billing</span></div>
        <button data-testid="mobile-menu-btn" onClick={() => setOpen(!open)} className="flex items-center gap-1.5 text-slate-200 px-2.5 py-1.5 rounded-lg border border-slate-700 bg-slate-800/60">{open ? <X size={18} /> : <Menu size={18} />}<span className="text-xs font-medium">Menu</span></button>
      </div>
      {open && (
        <div className="md:hidden fixed inset-0 z-40 bg-black/70" onClick={() => setOpen(false)}>
          <div className="w-64 h-full bg-[#0D1220] border-r border-slate-800" onClick={(e) => e.stopPropagation()}>
            {SideContent}
            <div className="p-3"><ChangePasswordButton /><Btn variant="ghost" className="w-full" onClick={doLogout}><LogOut size={16} /> Sign out</Btn></div>
          </div>
        </div>
      )}

      <main className="md:pl-64">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">{children}</div>
      </main>
    </div>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center bg-[#0B0F17] text-slate-500 font-mono">Loading…</div>;
  if (!user) return <Login />;
  return <Shell>{children}</Shell>;
}

function Landing() {
  const { user } = useAuth();
  if (user?.role === "staff") return <Navigate to="/invoices" replace />;
  return <Dashboard />;
}

function App() {
  return (
    <AuthProvider>
      <Toaster theme="dark" position="top-right" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Protected><Landing /></Protected>} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/invoices" element={<Protected><Invoices /></Protected>} />
          <Route path="/invoices/new" element={<Protected><InvoiceEditor /></Protected>} />
          <Route path="/invoices/:id/edit" element={<Protected><InvoiceEditor /></Protected>} />
          <Route path="/invoices/:id" element={<Protected><InvoiceDetail /></Protected>} />
          <Route path="/exports" element={<Protected><ExportInvoices /></Protected>} />
          <Route path="/exports/clients" element={<Protected><ExportClients /></Protected>} />
          <Route path="/exports/recurring" element={<Protected><ExportRecurring /></Protected>} />
          <Route path="/exports/new" element={<Protected><ExportInvoiceEditor /></Protected>} />
          <Route path="/exports/:id/edit" element={<Protected><ExportInvoiceEditor /></Protected>} />
          <Route path="/exports/:id" element={<Protected><ExportInvoiceDetail /></Protected>} />
          <Route path="/recurring" element={<Protected><Recurring /></Protected>} />
          <Route path="/clients" element={<Protected><Clients /></Protected>} />
          <Route path="/masters" element={<Protected><Masters /></Protected>} />
          <Route path="/tds" element={<Protected><TdsReport /></Protected>} />
          <Route path="/approvals" element={<Protected><Approvals /></Protected>} />
          <Route path="/users" element={<Protected><UsersPage /></Protected>} />
          <Route path="/audit" element={<Protected><Audit /></Protected>} />
          <Route path="/watchlist" element={<Protected><Watchlist /></Protected>} />
          <Route path="/settings" element={<Protected><Settings /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
